import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  ArrowUpwardRounded,
  ArrowDownwardRounded,
  ZoomInRounded,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import AuthenticatedImage from "components/AuthenticatedImage";
import { getTransactionDetail, evidenceUrl } from "api/financial";
import { getBranchCashTransactionDetail, branchCashEvidenceUrl } from "api/branchCash";
import { MONO, money, fmtDate, ParcelsTable, PaymentLines, SlipLightbox, SectionLabel, SOURCE_STYLE, SinglePaymentCard, ChangeGivenBox } from "./receiptParts";
import { formatPaymentSource } from "utils/formatWalletTxnDetail";

function Tile({ label, value, mono, full }) {
  return (
    <Box sx={{ gridColumn: full ? "1 / -1" : "auto", p: 1.25, borderRadius: "12px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#a8a3b4" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#2c2937", mt: 0.25, wordBreak: "break-word", fontFamily: mono ? MONO : undefined }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

// Transaction detail as a modal: a colored credit/debit hero, the ledger metadata,
// and — when the leg came from a shipping payment — the same receipt breakdown
// (parcels + payment slips) used on the Approval screen.
// `mode="financial"` (default) is the manager drill-down (/api/financial, any
// transaction). `mode="branch"` is the station-scoped Branch Cash page
// (/api/branch-cash, only the caller's own stations) — same UI, different endpoint.
export default function TransactionDetailDialog({ open, onClose, txId, mode = "financial" }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fetchDetail = mode === "branch" ? getBranchCashTransactionDetail : getTransactionDetail;
  const getEvidenceUrl = mode === "branch" ? branchCashEvidenceUrl : evidenceUrl;

  useEffect(() => {
    if (!open || !txId) return;
    setLoading(true);
    setD(null);
    fetchDetail(txId)
      .then((res) => setD(res && res.success ? res.data : null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, txId, mode]);

  const tx = d?.transaction;
  const credit = tx?.txn_type === "credit";
  // Multiple evidence photos are supported; older records only have the single
  // `evidencePhoto` — normalize both shapes. Read straight off the WalletTopupRequest
  // (d.topup) rather than the leg's own evidencePhoto: only the branch-cash leg of a
  // cash top-up ever gets one copied onto it (see approveTopupRequestCore), so reading
  // from the leg would show nothing when viewing the wallet-credit side instead.
  const topupPhotos = d?.topup
    ? (d.topup.evidencePhotos?.length ? d.topup.evidencePhotos : (d.topup.evidencePhoto?.filename ? [d.topup.evidencePhoto] : []))
    : [];
  const heroBg = credit
    ? "linear-gradient(135deg,#16a34a,#0f7a4f)"
    : "linear-gradient(135deg,#dc2626,#9b2c2c)";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{t("financial.txnDetail")}</Typography>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress color="secondary" /></Box>
        )}

        {!loading && tx && (
          <>
            {/* credit/debit hero */}
            <Box sx={{ p: "20px", borderRadius: "16px", color: "#fff", textAlign: "center", background: heroBg, boxShadow: "0 10px 24px -14px rgba(20,18,30,.5)" }}>
              <Chip
                size="small"
                icon={credit ? <ArrowDownwardRounded sx={{ color: "#fff !important", fontSize: 16 }} /> : <ArrowUpwardRounded sx={{ color: "#fff !important", fontSize: 16 }} />}
                label={(tx.txn_type || "").toUpperCase()}
                sx={{ bgcolor: "rgba(255,255,255,.22)", color: "#fff", fontWeight: 700, mb: 1 }}
              />
              <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 34, lineHeight: 1.1 }}>
                {credit ? "+" : "−"}{money(tx.amount, tx.currency)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, mt: 0.5, opacity: 0.9 }}>
                {tx.currency} · {tx.ledgerType}
              </Typography>
            </Box>

            {/* meta tiles */}
            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
              <Tile label={t("financial.txnId")} value={tx.id} mono />
              <Tile label={t("financial.type")} value={tx.purpose} />
              <Tile label={t("financial.balanceBefore")} value={money(tx.balanceBefore, tx.currency)} mono />
              <Tile label={t("financial.balanceAfter")} value={money(tx.balanceAfter, tx.currency)} mono />
              <Tile label={t("financial.actionBy")} value={tx.actionBy?.name} />
              <Tile label={t("financial.date")} value={fmtDate(tx.createdAt)} />
              {d.customer && (
                <Tile label={t("financial.customer")} value={`${d.customer.name}${d.customer.whatsapp ? ` · ${d.customer.whatsapp}` : ""}`} />
              )}
              {tx.stationId && <Tile label={t("financial.station")} value={tx.stationId} />}
              {tx.purpose === "deposit" && tx.paymentSource && (
                <Tile label={t("wallet.admin.paymentSource")} value={formatPaymentSource(tx.paymentSource, t)} />
              )}
              {tx.purpose === "deposit" && tx.stationName && (
                <Tile label={t("wallet.admin.selectStation")} value={tx.stationName} />
              )}
              {tx.purpose === "expense" && tx.remark && (
                <Tile label={t("branchCash.expenseReason")} value={tx.remark} full />
              )}
              {tx.description && tx.purpose !== "expense" && (
                <Tile label={t("financial.description")} value={tx.description} full />
              )}
              <Tile label={t("financial.reference")} value={tx.reference} mono full />
            </Box>

            {/* receipt — same breakdown as the Approval screen */}
            {d.receipt && (
              <>
                <Divider sx={{ my: 2.5 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 12 }} color="text.secondary">{d.receipt.receiptId}</Typography>
                </Divider>
                <ParcelsTable t={t} parcels={d.parcels || []} totalTHB={d.receipt.feeTHB} serviceFeeTHB={d.receipt.serviceFeeTHB} />
                <PaymentLines t={t} lines={d.receipt.lines || []} onEnlarge={setLightbox} getEvidenceUrl={getEvidenceUrl} />
              </>
            )}

            {/* cash top-up request — the full context behind a deposit/refund leg
                that came from a Top Up (received/credited amounts, evidence slip,
                change given, who requested/approved it), same position as the
                receipt section above. Requests approved before
                WalletTopupRequest.reference existed have no way back to this leg,
                so topup is simply absent for them. */}
            {!d.receipt && d.topup && (
              <>
                <Divider sx={{ my: 2.5 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 12 }} color="text.secondary">{t("wallet.admin.topUp")}</Typography>
                </Divider>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                  <Tile
                    label={t("wallet.admin.amountReceived")}
                    value={money(d.topup.receivedAmount ?? d.topup.amount, d.topup.receivedCurrency || d.topup.currency)}
                    mono
                  />
                  <Tile label={t("wallet.admin.amountCredited")} value={money(d.topup.amount, d.topup.currency)} mono />
                  {d.topup.paymentSource && (
                    <Tile label={t("wallet.admin.paymentSource")} value={formatPaymentSource(d.topup.paymentSource, t)} />
                  )}
                  {d.topup.stationId && <Tile label={t("wallet.admin.selectStation")} value={d.topup.stationId} />}
                  {d.topup.description && <Tile label={t("financial.description")} value={d.topup.description} full />}
                  {d.topup.createdByStaff?.name && (
                    <Tile label={t("branchCash.requestedBy")} value={d.topup.createdByStaff.name} />
                  )}
                  {d.topup.reviewedBy?.name && (
                    <Tile
                      label={t("financial.approvedBy")}
                      value={`${d.topup.reviewedBy.name}${d.topup.reviewedBy.reviewedAt ? ` · ${fmtDate(d.topup.reviewedBy.reviewedAt)}` : ""}`}
                    />
                  )}
                </Box>

                {/* payment slip — the evidence attached when the request was submitted */}
                <SectionLabel Icon={ReceiptLongOutlined}>{t("financial.paymentLines")}</SectionLabel>
                <SinglePaymentCard
                  t={t}
                  label={formatPaymentSource(d.topup.paymentSource, t)}
                  currency={d.topup.receivedCurrency || d.topup.currency}
                  amount={d.topup.receivedAmount ?? d.topup.amount}
                  style={SOURCE_STYLE[d.topup.paymentSource] || SOURCE_STYLE.cash_store}
                  photos={topupPhotos}
                  onEnlarge={setLightbox}
                  getEvidenceUrl={getEvidenceUrl}
                />

                {/* change given back to the customer — always cash from this branch's drawer for a top-up */}
                <ChangeGivenBox t={t} lines={d.topup.changeLines} />
              </>
            )}

            {/* standalone evidence (non-receipt, non-topup legs, e.g. cash deposit) */}
            {!d.receipt && !d.topup && d.evidenceFilename && (
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5, mb: 1 }}>{t("financial.evidence")}</Typography>
                <Box
                  onClick={() => setLightbox(d.evidenceFilename)}
                  sx={{ position: "relative", cursor: "zoom-in", border: "1px solid", borderColor: "divider", borderRadius: "14px", p: 1.5, "&:hover .slipHint": { opacity: 1 } }}
                >
                  <AuthenticatedImage
                    src={getEvidenceUrl(d.evidenceFilename)}
                    alt={t("financial.slip")}
                    style={{ width: "100%", height: "auto", maxHeight: "50vh", objectFit: "contain", borderRadius: 8, display: "block" }}
                  />
                  <Box
                    className="slipHint"
                    sx={{
                      position: "absolute", bottom: 14, right: 14,
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.5, borderRadius: "8px",
                      background: "rgba(20,18,30,.72)", color: "#fff", fontSize: 11, fontWeight: 600,
                      opacity: 0.85, transition: "opacity .15s", pointerEvents: "none",
                    }}
                  >
                    <ZoomInRounded sx={{ fontSize: 15 }} />
                    {t("financial.enlarge")}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}

        {!loading && !tx && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>{t("common.error")}</Typography>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.close")}
        </Button>
      </DialogActions>

      <SlipLightbox filename={lightbox} onClose={() => setLightbox(null)} t={t} getEvidenceUrl={getEvidenceUrl} />
    </Dialog>
  );
}
